"use client";

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from "react";
import { toast } from "@/lib/toast";
import { useWorkspaceContext } from "./workspaceContext";
import { getAllWorkAreas, createWorkArea, updateWorkArea, deleteWorkArea } from "@/services-frontend/workArea/workAreaServices";
import { WorkArea } from "@/types/workarea";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/hooks/use-auth";
import { createWorkareaHomePage } from "@/components/tailwind/template/workarea-home";

interface WorkAreaContextType {
  workAreas: WorkArea[];
  isLoading: boolean;
  refreshWorkAreas: (force?: boolean) => Promise<void>;
  createWorkspaceWorkArea: (name: string, description?: string, icon?: string, accessLevel?: "open" | "private") => Promise<WorkArea | null>;
  updateWorkspaceWorkArea: (workAreaId: string, name?: string, description?: string, icon?: string, accessLevel?: "open" | "private", memberAccess?: "full" | "edit" | "view") => Promise<void>;
  deleteWorkspaceWorkArea: (workAreaId: string) => Promise<void>;
}

const WorkAreaContext = createContext<WorkAreaContextType | undefined>(undefined);

export function WorkAreaProvider({ children }: { children: ReactNode }) {
  const [workAreas, setWorkAreas] = useState<WorkArea[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { currentWorkspace } = useWorkspaceContext();
  const { user } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const isFormPage = pathname?.startsWith('/form/');
  const lastFetchedWorkspaceIdRef = useRef<string | null>(null);
  // The workspace being fetched, not a bare flag — a boolean made a switch
  // mid-fetch return early and the new workspace was never fetched.
  const isFetchingRef = useRef<string | null>(null);
  const currentWorkspaceIdRef = useRef<string | undefined>(currentWorkspace?._id);
  currentWorkspaceIdRef.current = currentWorkspace?._id;

  const refreshWorkAreas = useCallback(async (force = false) => {
    const workspaceId = currentWorkspace?._id;
    if (!workspaceId) {
      setWorkAreas([]);
      lastFetchedWorkspaceIdRef.current = null;
      return;
    }

    // Prevent duplicate fetches for the same workspace unless forced
    if (!force && (isFetchingRef.current === workspaceId || lastFetchedWorkspaceIdRef.current === workspaceId)) {
      return;
    }

    isFetchingRef.current = workspaceId;
    setIsLoading(true);
    try {
      const fetchedWorkAreas = await getAllWorkAreas(workspaceId);
      // Switched away mid-flight — this response is for the old workspace.
      if (currentWorkspaceIdRef.current !== workspaceId) return;

      console.log("fetchedWorkAreas -------->", fetchedWorkAreas);
      // Normalize IDs - ensure both id and _id are present, and include icon/description
      const normalizedWorkAreas = fetchedWorkAreas.map((wa: any) => ({
        ...wa,
        id: wa.id || String(wa._id),
        _id: wa._id || wa.id || String(wa._id),
        icon: wa.icon || undefined,
        description: wa.description || undefined,
      }));
      setWorkAreas(normalizedWorkAreas);
      lastFetchedWorkspaceIdRef.current = workspaceId;
    } catch (error) {
      console.error("Error fetching work areas:", error);
      toast.error("Failed to fetch work areas");
    } finally {
      if (isFetchingRef.current === workspaceId) isFetchingRef.current = null;
      if (currentWorkspaceIdRef.current === workspaceId) setIsLoading(false);
    }
  }, [currentWorkspace?._id]);

  // Automatically fetch work areas when workspace becomes available
  // This handles the case where workspace loads after page reload
  useEffect(() => {
    // Skip API calls on form pages
    if (isFormPage) return;
    
    if (currentWorkspace?._id && lastFetchedWorkspaceIdRef.current !== currentWorkspace._id) {
      // Otherwise the previous workspace's areas stay up during the fetch,
      // and indefinitely if it fails.
      setWorkAreas([]);
      refreshWorkAreas();
    } else if (!currentWorkspace?._id) {
      // Clear work areas when workspace is cleared
      setWorkAreas([]);
      lastFetchedWorkspaceIdRef.current = null;
    }
  }, [currentWorkspace?._id, refreshWorkAreas, isFormPage]);

  const createWorkspaceWorkArea = useCallback(async (
    name: string,
    description?: string,
    icon?: string,
    accessLevel: "open" | "private" = "open"
  ): Promise<WorkArea | null> => {
    if (!currentWorkspace?._id) {
      toast.error("No workspace selected");
      return null;
    }

    try {
      const response = await createWorkArea({
        workspaceId: currentWorkspace._id,
        name,
        description,
        icon,
        accessLevel,
      });

      // Normalize ID and convert ObjectId fields to strings
      const normalizedWorkArea: WorkArea = {
        _id: String(response._id || response.id || ""),
        name: response.name,
        workspaceId: String(response.workspaceId || ""),
        orgDomain: response.orgDomain,
        accessLevel: response.accessLevel,
        memberAccess: response.memberAccess,
        ownerId: String(response.ownerId || ""),
        createdBy: String(response.createdBy || ""),
        createdAt: response.createdAt ? (typeof response.createdAt === 'string' ? response.createdAt : new Date(response.createdAt).toISOString()) : new Date().toISOString(),
        updatedAt: response.updatedAt ? (typeof response.updatedAt === 'string' ? response.updatedAt : new Date(response.updatedAt).toISOString()) : new Date().toISOString(),
        icon: response.icon || undefined,
        description: response.description || undefined,
        members: (response.members || []).map((m: any) => ({
          ...m,
          userId: String(m.userId || ""),
        })),
        groupAccess: (response.groupAccess || []).map((ga: any) => ({
          ...ga,
          groupId: String(ga.groupId || ""),
          grantedBy: String(ga.grantedBy || ""),
          grantedAt: ga.grantedAt instanceof Date ? ga.grantedAt : new Date(ga.grantedAt || Date.now()),
        })),
        requests: (response.requests || []).map((r: any) => ({
          ...r,
          userId: String(r.userId || ""),
          reviewedBy: r.reviewedBy ? String(r.reviewedBy) : undefined,
          createdAt: r.createdAt instanceof Date ? r.createdAt : new Date(r.createdAt || Date.now()),
          reviewedAt: r.reviewedAt ? (r.reviewedAt instanceof Date ? r.reviewedAt : new Date(r.reviewedAt)) : undefined,
        })),
      };

      // Add to list
      setWorkAreas((prev) => [...prev, normalizedWorkArea]);
      toast.success("Work area created successfully");

      // Seed the new workarea with the "Workarea home" template page.
      // Failure here must NOT roll back the workarea — the page is a courtesy,
      // not a requirement. We toast a non-blocking warning and continue.
      // Note: `addBlockToContext` is intentionally omitted — GlobalBlockProvider
      // sits below WorkAreaProvider in the tree, so the global block context
      // isn't reachable from here. The new page surfaces via the live IDB
      // query (`useLiveBlocks`) on the next render of the sidebar.
      if (normalizedWorkArea._id) {
        const userEmail = typeof user?.email === "string" ? user.email : "";
        try {
          const seedResult = await createWorkareaHomePage({
            workspaceId: currentWorkspace._id,
            workareaId: normalizedWorkArea._id,
            userEmail,
          });
          // Navigate the user straight to the newly-seeded home page so they
          // see the prepared content instead of an empty workarea.
          if (seedResult?.pageId) {
            router.push(`/notes/${seedResult.pageId}`);
          }
        } catch (seedError) {
          console.error("[workarea-home template] seed failed:", seedError);
          toast.message("Workarea created, but the home page couldn't be added.");
        }
      }

      return normalizedWorkArea;
    } catch (error) {
      console.error("Error creating work area:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create work area");
      return null;
    }
  }, [currentWorkspace?._id, user?.email, router]);

  const updateWorkspaceWorkArea = useCallback(async (
    workAreaId: string,
    name?: string,
    description?: string,
    icon?: string,
    accessLevel?: "open" | "private",
    memberAccess?: "full" | "edit" | "view"
  ): Promise<void> => {
    if (!currentWorkspace?._id) {
      toast.error("No workspace selected");
      return;
    }

    try {
      const response = await updateWorkArea({
        workAreaId,
        workspaceId: currentWorkspace._id,
        name,
        description,
        icon,
        accessLevel,
        memberAccess,
      });

      // Normalize ID and convert ObjectId fields to strings
      const normalizedWorkArea: WorkArea = {
        _id: String(response._id || response.id || ""),
        name: response.name,
        workspaceId: String(response.workspaceId || ""),
        orgDomain: response.orgDomain,
        accessLevel: response.accessLevel,
        memberAccess: response.memberAccess,
        ownerId: String(response.ownerId || ""),
        createdBy: String(response.createdBy || ""),
        createdAt: response.createdAt ? (typeof response.createdAt === 'string' ? response.createdAt : new Date(response.createdAt).toISOString()) : new Date().toISOString(),
        updatedAt: response.updatedAt ? (typeof response.updatedAt === 'string' ? response.updatedAt : new Date(response.updatedAt).toISOString()) : new Date().toISOString(),
        icon: response.icon || undefined,
        description: response.description || undefined,
        members: (response.members || []).map((m: any) => ({
          ...m,
          userId: String(m.userId || ""),
        })),
        groupAccess: (response.groupAccess || []).map((ga: any) => ({
          ...ga,
          groupId: String(ga.groupId || ""),
          grantedBy: String(ga.grantedBy || ""),
          grantedAt: ga.grantedAt instanceof Date ? ga.grantedAt : new Date(ga.grantedAt || Date.now()),
        })),
        requests: (response.requests || []).map((r: any) => ({
          ...r,
          userId: String(r.userId || ""),
          reviewedBy: r.reviewedBy ? String(r.reviewedBy) : undefined,
          createdAt: r.createdAt instanceof Date ? r.createdAt : new Date(r.createdAt || Date.now()),
          reviewedAt: r.reviewedAt ? (r.reviewedAt instanceof Date ? r.reviewedAt : new Date(r.reviewedAt)) : undefined,
        })),
      };

      // Update in list
      setWorkAreas((prev) =>
        prev.map((wa) => (wa._id === workAreaId || String(wa._id) === workAreaId ? normalizedWorkArea : wa))
      );
      toast.success("Work area updated successfully");
    } catch (error) {
      console.error("Error updating work area:", error);
      toast.error(error instanceof Error ? error.message : "Failed to update work area");
    }
  }, [currentWorkspace?._id]);

  const deleteWorkspaceWorkArea = useCallback(async (workAreaId: string): Promise<void> => {
    if (!currentWorkspace?._id) {
      toast.error("No workspace selected");
      return;
    }

    try {
      await deleteWorkArea({
        workAreaId,
        workspaceId: currentWorkspace._id,
      });

      // Remove from list
      setWorkAreas((prev) => prev.filter((wa) => wa._id !== workAreaId && String(wa._id) !== workAreaId));
      toast.success("Work area deleted successfully");
    } catch (error) {
      console.error("Error deleting work area:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete work area");
    }
  }, [currentWorkspace?._id]);

  return (
    <WorkAreaContext.Provider
      value={{
        workAreas,
        isLoading,
        refreshWorkAreas,
        createWorkspaceWorkArea,
        updateWorkspaceWorkArea,
        deleteWorkspaceWorkArea,
      }}
    >
      {children}
    </WorkAreaContext.Provider>
  );
}

export function useWorkAreaContext() {
  const context = useContext(WorkAreaContext);
  if (context === undefined) {
    throw new Error("useWorkAreaContext must be used within a WorkAreaProvider");
  }
  return context;
}

